import type { ScaleQuantize } from 'd3-scale';

import { useChartLayoutOrientation } from '../../../../core/components/chart-layout/hooks/useChartLayoutOrientation.js';
import { getLegendOrientation } from '../../../../core/components/chart-layout/utils/orientation.js';
import { RangeLegend } from '../../../../core/components/legend/range/RangeLegend.js';
import { isColoredRangeArray } from '../../../../core/types/colored-range.js';
import { buildLegendItemsFromRange } from '../../../../core/utils/build-legend-items-from-range.js';
import { buildQuantizedRangeItems } from '../../../../core/utils/build-quantized-range-items.js';
import { getFormatter } from '../../../../core/utils/get-formatter.js';
import { safeGet } from '../../../../core/utils/safe-get.js';
import { useRectData } from '../../../hooks/useRectData.js';
import { useXYChartColorScales } from '../../../hooks/useXYChartColorScales.js';
import { useXYChartTruncationMode } from '../../../hooks/useXYChartTruncationMode.js';
import type { RectSeriesWithDatapoints } from '../../../types/xy-chart-internals.js';

export const RectRangeLegend = ({
  series,
}: {
  series: RectSeriesWithDatapoints;
}) => {
  const { orientation: layoutOrientation } = useChartLayoutOrientation();
  const { seriesToMetadata } = useRectData();
  const orientation = getLegendOrientation(layoutOrientation);
  const scaleColorMap = useXYChartColorScales();
  const { truncationMode } = useXYChartTruncationMode();

  const {
    colorPalette: rangeColorPalette,
    valueFormatter,
    valueUnit,
  } = safeGet(seriesToMetadata, series);

  let items;
  if (isColoredRangeArray(rangeColorPalette)) {
    const rangeItems = [...rangeColorPalette];
    items = buildLegendItemsFromRange(rangeItems);
  } else {
    items = buildQuantizedRangeItems(
      safeGet(scaleColorMap, series.id) as ScaleQuantize<string>,
      //Match the reverse logic that is build for plot
      true,
    );
  }

  return (
    <RangeLegend
      items={items}
      orientation={orientation}
      formatter={(value) => getFormatter(valueFormatter)(value, valueUnit)}
      truncationMode={truncationMode}
    />
  );
};
